import React, { useState, useEffect, useCallback } from 'react';
import { Sparkle, X, CaretRight } from '@phosphor-icons/react';

const KIND_STYLES = {
  guest:   { tint: 'rgba(236,72,153,0.10)', border: 'rgba(236,72,153,0.30)', text: '#F9A8D4', accent: '#EC4899' },
  family:  { tint: 'rgba(34,197,94,0.10)',  border: 'rgba(34,197,94,0.30)',  text: '#86EFAC', accent: '#22C55E' },
  pattern: { tint: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.30)', text: '#FCD34D', accent: '#F59E0B' },
};

export default function BriefCard({ api, onActionTrigger }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);

  const fetchBrief = useCallback(async () => {
    try {
      const r = await api.get('/meals/brief');
      setItems(r.data?.items || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchBrief(); }, [fetchBrief]);

  const handleDismiss = async (item) => {
    setDismissing(item.brief_id);
    try {
      await api.post('/meals/brief/dismiss', {
        brief_id: item.brief_id,
        kind: item.kind,
        key: item.key || '',
        days: 7,
      });
      setItems(prev => prev.filter(x => x.brief_id !== item.brief_id));
    } catch (_) { /* ignore */ }
    setDismissing(null);
  };

  const handleAction = (item) => {
    if (onActionTrigger) onActionTrigger(item);
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <div className="space-y-2" data-testid="brief-card-strip">
      <div className="flex items-center gap-2 px-1">
        <Sparkle size={12} weight="fill" className="text-purple-300" />
        <p className="text-[10px] uppercase tracking-wider font-bold text-purple-300">AI Chef Brief</p>
        <p className="text-[10px] text-neutral-500">· {items.length} suggestion{items.length === 1 ? '' : 's'}</p>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x">
        {items.map(it => {
          const style = KIND_STYLES[it.kind] || KIND_STYLES.pattern;
          return (
            <div key={it.brief_id} data-testid={`brief-item-${it.brief_id}`}
              className="snap-start min-w-[280px] sm:min-w-[320px] max-w-[360px] rounded-2xl p-3.5 flex flex-col"
              style={{ background: style.tint, border: `1px solid ${style.border}` }}>
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-xl flex-shrink-0">{it.icon}</span>
                  <p className="text-[9px] uppercase tracking-wider font-bold flex-shrink-0" style={{ color: style.text }}>
                    {it.kind}
                  </p>
                </div>
                <button onClick={() => handleDismiss(it)} disabled={dismissing === it.brief_id}
                  data-testid={`brief-dismiss-${it.brief_id}`}
                  title="Hide for 7 days"
                  className="p-1 rounded-lg text-neutral-500 hover:text-red-300 hover:bg-white/5 transition-colors flex-shrink-0 disabled:opacity-40">
                  <X size={11} weight="bold" />
                </button>
              </div>
              <p className="text-[13px] font-bold text-white leading-tight mb-1">{it.headline}</p>
              <p className="text-[11px] text-neutral-300 leading-snug flex-1 mb-2">{it.reason}</p>
              <button onClick={() => handleAction(it)} data-testid={`brief-action-${it.brief_id}`}
                className="self-start inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-bold transition-colors"
                style={{ background: style.accent, color: '#fff' }}>
                {it.action_label} <CaretRight size={10} weight="bold" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
